/**
 * PMS Query Module
 *
 * Provides a Supabase-compatible query builder interface that routes
 * through the composite data provider (WP → Supabase fallback).
 *
 * Standard CRUD operations (select/insert/update/delete) go through
 * compositeDataProvider so WordPress is tried first.
 *
 * For complex queries that can't be expressed through the DP pattern
 * (e.g. joins, head-only counts, .maybeSingle(), .in() filters),
 * the builder falls back to the raw Supabase client automatically.
 */

import { supabase } from '@/integrations/supabase/client';
import { compositeDataProvider } from './compositeDataProvider';
import { WP_ENABLED } from './config';

type EqFilter = { column: string; value: unknown };
type InFilter = { column: string; values: unknown[] };

interface PmsQueryBuilder {
  // Chainable filters
  select(columns?: string): this;
  eq(column: string, value: unknown): this;
  neq(column: string, value: unknown): this;
  in(column: string, values: unknown[]): this;
  order(column: string, opts?: { ascending?: boolean }): this;
  limit(n: number): this;
  maybeSingle(): Promise<{ data: unknown; error: null }>;
  single(): Promise<{ data: unknown; error: unknown }>;
  // Terminal — returns { data, error }
  then(
    onfulfilled: (result: { data: unknown[]; error: null }) => unknown,
    onrejected?: (reason: unknown) => unknown,
  ): Promise<unknown>;
}

/**
 * Build a composite-aware query builder for a table.
 *
 * Simple getList / getOne calls are routed through compositeDataProvider
 * (WP first, Supabase fallback). Complex queries (joins, head-only,
 * .maybeSingle()) fall through to the raw Supabase client.
 */
export function pmsFrom(table: string): any {
  // If WP is disabled, just pass through directly.
  if (!WP_ENABLED) {
    return (supabase as any).from(table);
  }

  let _selectColumns = '*';
  let _selectOpts: { count?: string; head?: boolean } | undefined;
  let _eqFilters: EqFilter[] = [];
  let _inFilters: InFilter[] = [];
  let _neqFilters: EqFilter[] = [];
  let _orderColumn: string | null = null;
  let _orderAsc = true;
  let _limit: number | null = null;
  let _isMaybeSingle = false;
  let _isSingle = false;
  let _isComplex = false; // joins, head-only, etc.

  const builder: any = {
    select(columns = '*', opts?: { count?: string; head?: boolean }) {
      _selectColumns = columns;
      _selectOpts = opts;
      // HEAD / count queries must use raw Supabase
      if (opts?.head || opts?.count) {
        _isComplex = true;
      }
      // Detect joins (embedded selects like 'jobs(title)')
      if (typeof columns === 'string' && columns !== '*' && columns.includes('(')) _isComplex = true;
      return builder;
    },

    eq(column: string, value: unknown) {
      _eqFilters.push({ column, value });
      return builder;
    },

    neq(column: string, value: unknown) {
      _neqFilters.push({ column, value });
      _isComplex = true; // neq not in DP filter API — use raw
      return builder;
    },

    in(column: string, values: unknown[]) {
      _inFilters.push({ column, values });
      _isComplex = true; // .in() not in DP filter API — use raw
      return builder;
    },

    order(column: string, opts?: { ascending?: boolean }) {
      _orderColumn = column;
      _orderAsc = opts?.ascending ?? true;
      return builder;
    },

    limit(n: number) {
      _limit = n;
      return builder;
    },

    maybeSingle() {
      _isMaybeSingle = true;
      return builder._executeRaw();
    },

    single() {
      _isSingle = true;
      return builder._executeRaw();
    },

    update(data: Record<string, unknown>) {
      // Returns a sub-builder for update+eq
      const updateFilters: EqFilter[] = [..._eqFilters];
      const updateInFilters: InFilter[] = [..._inFilters];
      return {
        eq(col: string, val: unknown) {
          updateFilters.push({ col, val } as any);
          return this;
        },
        in(col: string, vals: unknown[]) {
          updateInFilters.push({ column: col, values: vals });
          return this;
        },
        async then(resolve: any, reject: any) {
          try {
            // For updates we need a single id — try composite DP
            const idFilter = updateFilters.find(f => f.column === 'id') as any;
            if (idFilter && !updateInFilters.length) {
              const result = await compositeDataProvider.update(table, {
                id: idFilter.value as string,
                data,
              });
              return resolve({ data: result.data, error: null });
            }
            // Fallback: raw supabase for bulk updates
            let q = (supabase as any).from(table).update(data);
            for (const f of updateFilters) {
              q = q.eq((f as any).column ?? (f as any).col, (f as any).value ?? (f as any).val);
            }
            for (const f of updateInFilters) {
              q = q.in(f.column, f.values);
            }
            const result = await q;
            return resolve(result);
          } catch (err) {
            if (reject) return reject(err);
            throw err;
          }
        },
      };
    },

    insert(data: Record<string, unknown> | Record<string, unknown>[]) {
      return {
        async then(resolve: any, reject: any) {
          try {
            const records = Array.isArray(data) ? data : [data];
            for (const record of records) {
              await compositeDataProvider.create(table, { data: record });
            }
            return resolve({ data: null, error: null });
          } catch (err) {
            if (reject) return reject(err);
            throw err;
          }
        },
      };
    },

    delete() {
      const deleteFilters: EqFilter[] = [..._eqFilters];
      const deleteInFilters: InFilter[] = [..._inFilters];
      return {
        eq(col: string, val: unknown) {
          deleteFilters.push({ column: col, value: val });
          return this;
        },
        in(col: string, vals: unknown[]) {
          deleteInFilters.push({ column: col, values: vals });
          return this;
        },
        async then(resolve: any, reject: any) {
          try {
            const idFilter = deleteFilters.find(f => f.column === 'id');
            if (idFilter && !deleteInFilters.length) {
              await compositeDataProvider.delete(table, { id: idFilter.value as string });
              return resolve({ data: null, error: null });
            }
            // Bulk delete via deleteMany
            if (deleteInFilters.length) {
              const idInFilter = deleteInFilters.find(f => f.column === 'id');
              if (idInFilter) {
                await compositeDataProvider.deleteMany(table, {
                  ids: idInFilter.values as string[],
                });
                return resolve({ data: null, error: null });
              }
            }
            // Fallback to raw
            let q = (supabase as any).from(table).delete();
            for (const f of deleteFilters) q = q.eq(f.column, f.value);
            for (const f of deleteInFilters) q = q.in(f.column, f.values);
            const result = await q;
            return resolve(result);
          } catch (err) {
            if (reject) return reject(err);
            throw err;
          }
        },
      };
    },

    /** Use raw Supabase for complex queries */
    async _executeRaw() {
      let q = (supabase as any).from(table).select(_selectColumns, _selectOpts);
      for (const f of _eqFilters) q = q.eq(f.column, f.value);
      for (const f of _neqFilters) q = q.neq(f.column, f.value);
      for (const f of _inFilters) q = q.in(f.column, f.values);
      if (_orderColumn) q = q.order(_orderColumn, { ascending: _orderAsc });
      if (_limit !== null) q = q.limit(_limit);
      if (_isMaybeSingle) q = q.maybeSingle();
      else if (_isSingle) q = q.single();
      return q;
    },

    /** Thenable — executes the query when awaited */
    async then(resolve: any, reject: any) {
      try {
        // Complex queries always use raw Supabase
        if (_isComplex) {
          const result = await builder._executeRaw();
          return resolve(result);
        }

        // Build DP filter map from eq filters
        const filter: Record<string, unknown> = {};
        for (const f of _eqFilters) {
          filter[f.column] = f.value;
        }

        const dpParams: any = {
          filter: Object.keys(filter).length ? filter : undefined,
          sort: _orderColumn
            ? { field: _orderColumn, order: _orderAsc ? 'ASC' : 'DESC' }
            : undefined,
          pagination: _limit ? { page: 1, perPage: _limit } : undefined,
        };

        const result = await compositeDataProvider.getList(table, dpParams);
        return resolve({ data: result.data, error: null });
      } catch (err) {
        if (reject) return reject(err);
        throw err;
      }
    },
  };

  return builder;
}
